import React, { useEffect, useRef, useState } from 'react';

import {
  MaximizeIcon, MinimizeIcon, RefreshRightIcon, SettingsIcon,
} from '@vlabs/icons';
import { GoToEditStreamIconButton } from '@vlabs/monokit/features/go-to-edit-stream';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import { ButtonScrimGroup, Control } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import Hls from 'hls.js';

import { useSourceLivePageTranslation } from '../i18n';
import { useRestream } from '../restream-context';
import { StreamErrorStub } from './StreamErrorStub';
import { StreamMapModal } from './StreamMapModal';
import st from './StreamPlayer.module.sass';
import { StreamQualitySelect } from './StreamQualitySelect';

type PlayerStatus = 'connecting' | 'playing' | 'failed';

const MANIFEST_RETRY_DELAY_MS = 5000;
const MAX_MANIFEST_RETRIES = 20;

// Плеер ретрансляции: самодостаточен — подключение (url/token/переподключение)
// и качество берутся из контекста ретрансляции; fullscreen-панель собирается внутри.
function StreamPlayer(): React.JSX.Element {
  const { t } = useSourceLivePageTranslation();
  const {
    streamId, retransmission, attempt, reconnect,
  } = useRestream();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playerStatus, setPlayerStatus] = useState<PlayerStatus>('connecting');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const manifestRetryCountRef = useRef(0);
  const lastUrlRef = useRef<string | undefined>(undefined);

  const url = retransmission?.url;
  const token = retransmission?.token;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return undefined;

    // Новая сессия (url сменился) — счётчик ретраев манифеста с нуля
    if (lastUrlRef.current !== url) {
      lastUrlRef.current = url;
      manifestRetryCountRef.current = 0;
    }

    setPlayerStatus('connecting');

    if (!Hls.isSupported()) {
      setPlayerStatus('failed');
      return undefined;
    }

    const hls = new Hls({
      xhrSetup: (xhr) => {
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        // eslint-disable-next-line no-param-reassign
        xhr.withCredentials = false;
      },
    });

    hls.loadSource(url);
    hls.attachMedia(video);

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      setPlayerStatus('playing');
      // Автоплей при подключении: видео muted — политика автовоспроизведения не мешает
      video.play().catch(() => {});
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;
      hls.destroy();

      const isManifestLoadError = data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR
        || data.details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT;
      // Сессия ретранслятора прогревается после создания (особенно при смене качества/транскодинге)
      // и манифест какое-то время 404. hls.js 4xx не ретраит — ретраим подключение сами,
      // чтобы плеер пережил прогрев вместо мгновенной заглушки
      if (isManifestLoadError && manifestRetryCountRef.current < MAX_MANIFEST_RETRIES) {
        manifestRetryCountRef.current += 1;
        window.setTimeout(() => setRetryTick((value) => value + 1), MANIFEST_RETRY_DELAY_MS);
        return;
      }

      setPlayerStatus('failed');
    });

    return () => hls.destroy();
    // attempt — пересоздание подключения по кнопке «Подключение»; retryTick — ретрай манифеста
  }, [url, token, attempt, retryTick]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen();
    }
  };

  const isFailed = playerStatus === 'failed';

  let fullscreenIcon: React.JSX.Element = <MaximizeIcon />;
  let fullscreenLabel = t('fullscreen_enter');
  if (isFullscreen) {
    fullscreenIcon = <MinimizeIcon />;
    fullscreenLabel = t('fullscreen_exit');
  }

  return (
    <div
      className={st.Root}
      ref={containerRef}
    >
      <ButtonScrimGroup className={st.FullscreenButton}>
        <IconButton
          aria-label={fullscreenLabel}
          icon={fullscreenIcon}
          kind="secondary"
          onClick={toggleFullscreen}
          title={fullscreenLabel}
          variant="flat"
        />
      </ButtonScrimGroup>

      {/* Видео всегда в DOM: при ошибке прячется hidden, а не размонтируется —
          иначе videoRef пуст и эффект не может перезапустить плеер по «Подключению» */}
      <video
        aria-label={t('translation')}
        className={st.Video}
        hidden={isFailed}
        muted
        playsInline
        ref={videoRef}
      />

      {playerStatus === 'connecting' && (
        <div className={st.LoaderOverlay}>
          <Loader />
        </div>
      )}

      {!!isFailed && <StreamErrorStub withReconnect />}

      <StreamMapModal streamId={streamId} />

      {!!isFullscreen && (
        <div className={st.SettingsBar}>
          <StreamQualitySelect />
          <Control.Button
            icon={<RefreshRightIcon />}
            kind="primary"
            onClick={reconnect}
          >
            {t('connect')}
          </Control.Button>
          <GoToEditStreamIconButton
            icon={<SettingsIcon />}
            kind="primary"
            shape="square"
            streamId={streamId}
            variant="outlined"
          />
        </div>
      )}
    </div>
  );
}

export { StreamPlayer };
